/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import listAllSaga from './listAll';
import byIdSaga, { CategoryByIdData } from './byId';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { FileData, PaginatedResponse } from '../types';
import { Comment } from '../comment';

export const moduleName = 'category';

export interface SubCategoryShort {
  id: string;
  isActive: boolean;
  header: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryAndSubCategory extends CategoryShort {
  subCategories: SubCategoryShort[];
}

export interface CategoryShort {
  id: string;
  isActive: boolean;
  name: string;
  header: string | null;
  shortDescription?: string | null;
  description: string | null;
  author: string | null;
  file: FileData | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubCategoryStatistic {
  id: string;
  header: string;
  isActive: boolean;
  description: string;
  category: {
    id: string;
    name: string;
    isActive: boolean;
  },
  statisticByCustomer?: {
    medianValue: number;
    avgValue: number;
    count: number;
  },
  statisticByWorker?: {
    medianValue: number;
    avgValue: number;
    count: number;
  }
}

export interface CategoryStatistic {
  id: string;
  name: string;
  isActive: boolean;
  header: string;
  description: string;
  shortDescription: string;
  author: string;
  file: FileData;
  comments: Comment[],
  subCategories: SubCategoryStatistic[]
}

export interface State {
  list: BaseState<PaginatedResponse<CategoryShort>>,
  listAll: BaseState<PaginatedResponse<CategoryAndSubCategory>>,
  byId: BaseState<CategoryByIdData>,
  create: BaseState,
  update: BaseState,
  delete: BaseState
}

const models = ['list', 'listAll', 'byId', 'statistic', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const category = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = category;

export function useCategory(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* listAllSaga();
  yield* byIdSaga();
}
